import { useCallback, useEffect, useId, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import type { Database } from '@kit/supabase/database';
import { useSupabase } from '@kit/supabase/hooks/use-supabase';

type CommunityNotification =
  Database['public']['Tables']['community_notifications']['Row'];
type NotificationRecipient =
  Database['public']['Tables']['notification_recipients']['Row'];

export interface CommunityNotificationWithRecipient extends CommunityNotification {
  recipient: NotificationRecipient | null;
}

interface UseCommunityNotificationsOptions {
  profileId?: string;
  organizationId?: string;
  limit?: number;
  realtime?: boolean;
}

/**
 * Hook to fetch and subscribe to community notifications
 */
export function useCommunityNotifications(
  options: UseCommunityNotificationsOptions,
) {
  const { profileId, organizationId, limit = 20, realtime = true } = options;
  const client = useSupabase();
  const queryClient = useQueryClient();
  const channelRef = useRef<ReturnType<typeof client.channel> | null>(null);
  // Per-mount channel id so React Strict Mode / concurrent re-renders never
  // reuse a channel that has already had `.subscribe()` called on it.
  // `client.channel(name)` returns the same singleton for the same name; a
  // second mount calling `.on()` on it triggers
  // `cannot add postgres_changes callbacks for realtime:<name> after subscribe()`.
  const channelId = useId();

  // Query key for cache management - memoized to prevent unnecessary effect re-runs
  const queryKey = useMemo(
    () => ['community-notifications', profileId, organizationId],
    [profileId, organizationId],
  );

  // Fetch notifications
  const query = useQuery({
    queryKey,
    queryFn: async (): Promise<CommunityNotificationWithRecipient[]> => {
      if (!profileId && !organizationId) {
        return [];
      }

      let queryBuilder = client
        .from('notification_recipients')
        .select(
          `
          *,
          notification:community_notifications!notification_id(*)
        `,
        )
        .is('dismissed_at', null)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (profileId) {
        queryBuilder = queryBuilder.eq(
          'recipient_professional_profile_id',
          profileId,
        );
      } else if (organizationId) {
        queryBuilder = queryBuilder.eq(
          'recipient_organization_id',
          organizationId,
        );
      }

      const { data, error } = await queryBuilder;

      if (error) {
        throw error;
      }

      // Transform result to expected format. The embedded `notification`
      // (a to-one join on notification_id) and the recipient row are already
      // typed off the generated Database type, so no unsafe cast is needed.
      return (data || []).map((item): CommunityNotificationWithRecipient => {
        const { notification, ...recipient } = item;

        return {
          ...(notification as CommunityNotification),
          recipient: recipient as NotificationRecipient,
        };
      });
    },
    enabled: !!(profileId || organizationId),
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    staleTime: 30000, // 30 seconds
  });

  // Set up realtime subscription
  useEffect(() => {
    if (!realtime || (!profileId && !organizationId)) {
      return;
    }

    // Clean up existing subscription
    if (channelRef.current) {
      client.removeChannel(channelRef.current);
    }

    // Create new subscription on a per-mount channel name to avoid the
    // SDK's name-keyed singleton cache.
    const channel = client
      .channel(`community-notifications-${channelId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notification_recipients',
          filter: profileId
            ? `recipient_professional_profile_id=eq.${profileId}`
            : `recipient_organization_id=eq.${organizationId}`,
        },
        () => {
          // Invalidate query to refetch notifications
          queryClient.invalidateQueries({ queryKey });
        },
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notification_recipients',
          filter: profileId
            ? `recipient_professional_profile_id=eq.${profileId}`
            : `recipient_organization_id=eq.${organizationId}`,
        },
        () => {
          // Invalidate query to refetch notifications
          queryClient.invalidateQueries({ queryKey });
        },
      )
      .subscribe();

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        client.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [
    client,
    profileId,
    organizationId,
    realtime,
    queryClient,
    queryKey,
    channelId,
  ]);

  return query;
}

/**
 * Hook to get unread notification count
 */
export function useCommunityUnreadCount(options: {
  profileId?: string;
  organizationId?: string;
}) {
  const { profileId, organizationId } = options;
  const client = useSupabase();

  return useQuery({
    queryKey: [
      'community-notifications-unread-count',
      profileId,
      organizationId,
    ],
    queryFn: async (): Promise<number> => {
      if (!profileId && !organizationId) {
        return 0;
      }

      let queryBuilder = client
        .from('notification_recipients')
        .select('*', { count: 'exact', head: true })
        .is('read_at', null)
        .is('dismissed_at', null);

      if (profileId) {
        queryBuilder = queryBuilder.eq(
          'recipient_professional_profile_id',
          profileId,
        );
      } else if (organizationId) {
        queryBuilder = queryBuilder.eq(
          'recipient_organization_id',
          organizationId,
        );
      }

      const { count, error } = await queryBuilder;

      if (error) {
        throw error;
      }

      return count || 0;
    },
    enabled: !!(profileId || organizationId),
    refetchInterval: 60000, // Refetch every minute
    staleTime: 30000, // 30 seconds
  });
}

/**
 * Hook to mark notifications as read
 */
export function useMarkCommunityNotificationsAsRead() {
  const client = useSupabase();
  const queryClient = useQueryClient();

  return useCallback(
    async (notificationIds: string[]) => {
      if (notificationIds.length === 0) {
        return 0;
      }

      const { data, error } = await client
        .from('notification_recipients')
        .update({ read_at: new Date().toISOString() })
        .in('notification_id', notificationIds)
        .is('read_at', null)
        .select('id');

      if (error) {
        throw error;
      }

      // Invalidate related queries
      queryClient.invalidateQueries({
        queryKey: ['community-notifications'],
      });
      queryClient.invalidateQueries({
        queryKey: ['community-notifications-unread-count'],
      });

      return data?.length || 0;
    },
    [client, queryClient],
  );
}

/**
 * Hook to mark all notifications as read
 */
export function useMarkAllCommunityNotificationsAsRead() {
  const client = useSupabase();
  const queryClient = useQueryClient();

  return useCallback(
    async (options: { profileId?: string; organizationId?: string }) => {
      const { profileId, organizationId } = options;

      if (!profileId && !organizationId) {
        return 0;
      }

      let queryBuilder = client
        .from('notification_recipients')
        .update({ read_at: new Date().toISOString() })
        .is('read_at', null);

      if (profileId) {
        queryBuilder = queryBuilder.eq(
          'recipient_professional_profile_id',
          profileId,
        );
      } else if (organizationId) {
        queryBuilder = queryBuilder.eq(
          'recipient_organization_id',
          organizationId,
        );
      }

      const { data, error } = await queryBuilder.select('id');

      if (error) {
        throw error;
      }

      // Invalidate related queries
      queryClient.invalidateQueries({
        queryKey: ['community-notifications'],
      });
      queryClient.invalidateQueries({
        queryKey: ['community-notifications-unread-count'],
      });

      return data?.length || 0;
    },
    [client, queryClient],
  );
}

/**
 * Hook to dismiss a notification
 */
export function useDismissCommunityNotification() {
  const client = useSupabase();
  const queryClient = useQueryClient();

  return useCallback(
    async (notificationId: string) => {
      const { error } = await client
        .from('notification_recipients')
        .update({ dismissed_at: new Date().toISOString() })
        .eq('notification_id', notificationId);

      if (error) {
        throw error;
      }

      // Invalidate related queries
      queryClient.invalidateQueries({
        queryKey: ['community-notifications'],
      });
      queryClient.invalidateQueries({
        queryKey: ['community-notifications-unread-count'],
      });

      return true;
    },
    [client, queryClient],
  );
}
